import { apiRequest } from "./client";

export type CourseStatus = "draft" | "published" | "archived";
export type LessonType = "text" | "video" | "file" | "checklist" | "quiz" | "mixed";

export interface CourseListItem {
  id: number;
  title: string;
  slug: string;
  status: CourseStatus;
  is_mandatory: boolean;
  estimated_duration_minutes: number | null;
  cover_file_id: string | null;
  published_at: string | null;
  updated_at: string;
  lessons_count: number;
}

export interface ModuleListItem {
  id: number;
  title: string;
  description: string | null;
  position: number;
  is_required: boolean;
  final_quiz_id: number | null;
}

export interface ModuleLessonRef {
  lesson_id: number;
  position: number;
  title: string;
  slug: string;
  lesson_type: LessonType;
  estimated_minutes: number | null;
}

export interface ModuleDetail extends ModuleListItem {
  lessons: ModuleLessonRef[];
  created_at: string;
  updated_at: string;
}

export interface TagOut {
  id: number;
  name: string;
  slug: string;
}

export interface CourseDetail extends CourseListItem {
  description: string | null;
  requires_curator_exam: boolean;
  final_quiz_id: number | null;
  modules: ModuleListItem[];
  tags: TagOut[];
  created_at: string;
}

export interface CourseList {
  items: CourseListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface LessonListItem {
  id: number;
  title: string;
  internal_title: string | null;
  slug: string;
  lesson_type: LessonType;
  estimated_minutes: number | null;
  is_required_for_completion: boolean;
  version: number;
  updated_at: string;
}

export interface LessonDetail extends LessonListItem {
  content_markdown: string | null;
  video_url: string | null;
  quiz_id: number | null;
  tags: TagOut[];
  created_at: string;
}

export interface LessonList {
  items: LessonListItem[];
  total: number;
  page: number;
  page_size: number;
}

// ─── Courses ─────────────────────────────────────────────────────────

export function listCourses(params: {
  status?: CourseStatus;
  q?: string;
  page?: number;
  page_size?: number;
} = {}): Promise<CourseList> {
  const u = new URLSearchParams();
  if (params.status) u.set("status", params.status);
  if (params.q) u.set("q", params.q);
  if (params.page) u.set("page", String(params.page));
  if (params.page_size) u.set("page_size", String(params.page_size));
  const s = u.toString();
  return apiRequest<CourseList>(`/courses${s ? `?${s}` : ""}`);
}

export function getCourse(id: number): Promise<CourseDetail> {
  return apiRequest<CourseDetail>(`/courses/${id}`);
}

export function createCourse(payload: {
  title: string;
  slug: string;
  description?: string;
  estimated_duration_minutes?: number;
  is_mandatory?: boolean;
  requires_curator_exam?: boolean;
}): Promise<CourseDetail> {
  return apiRequest<CourseDetail>("/courses", { method: "POST", body: payload });
}

export function updateCourse(
  id: number,
  payload: Partial<{
    title: string;
    description: string;
    estimated_duration_minutes: number;
    is_mandatory: boolean;
    requires_curator_exam: boolean;
    cover_file_id: string | null;
  }>,
): Promise<CourseListItem> {
  // Backend interprets cover_file_id="" as "clear cover"; map JS null → "".
  const body =
    "cover_file_id" in payload && payload.cover_file_id === null
      ? { ...payload, cover_file_id: "" }
      : payload;
  return apiRequest<CourseListItem>(`/courses/${id}`, { method: "PATCH", body });
}

export function publishCourse(id: number): Promise<CourseListItem> {
  return apiRequest<CourseListItem>(`/courses/${id}/publish`, { method: "POST" });
}

export function archiveCourse(id: number): Promise<CourseListItem> {
  return apiRequest<CourseListItem>(`/courses/${id}/archive`, { method: "POST" });
}

export function deleteCourse(id: number): Promise<void> {
  return apiRequest<void>(`/courses/${id}`, { method: "DELETE" });
}

// ─── Modules ─────────────────────────────────────────────────────────

export function createModule(
  courseId: number,
  payload: { title: string; description?: string; is_required?: boolean },
): Promise<ModuleListItem> {
  return apiRequest<ModuleListItem>(`/courses/${courseId}/modules`, {
    method: "POST",
    body: payload,
  });
}

export function getModule(id: number): Promise<ModuleDetail> {
  return apiRequest<ModuleDetail>(`/modules/${id}`);
}

export function updateModule(
  id: number,
  payload: Partial<{ title: string; description: string; is_required: boolean }>,
): Promise<ModuleListItem> {
  return apiRequest<ModuleListItem>(`/modules/${id}`, { method: "PATCH", body: payload });
}

export function deleteModule(id: number): Promise<void> {
  return apiRequest<void>(`/modules/${id}`, { method: "DELETE" });
}

export function attachLesson(
  moduleId: number,
  lessonId: number,
  position?: number,
): Promise<void> {
  return apiRequest<void>(`/modules/${moduleId}/lessons`, {
    method: "POST",
    body: { lesson_id: lessonId, ...(position !== undefined ? { position } : {}) },
  });
}

export function detachLesson(moduleId: number, lessonId: number): Promise<void> {
  return apiRequest<void>(`/modules/${moduleId}/lessons/${lessonId}`, {
    method: "DELETE",
  });
}

// ─── Lessons ─────────────────────────────────────────────────────────

export function listLessons(params: {
  q?: string;
  page?: number;
  page_size?: number;
} = {}): Promise<LessonList> {
  const u = new URLSearchParams();
  if (params.q) u.set("q", params.q);
  if (params.page) u.set("page", String(params.page));
  if (params.page_size) u.set("page_size", String(params.page_size));
  const s = u.toString();
  return apiRequest<LessonList>(`/lessons${s ? `?${s}` : ""}`);
}

export function getLesson(id: number): Promise<LessonDetail> {
  return apiRequest<LessonDetail>(`/lessons/${id}`);
}

export function createLesson(payload: {
  title: string;
  slug: string;
  lesson_type?: LessonType;
  internal_title?: string;
  content_markdown?: string;
  video_url?: string;
  estimated_minutes?: number;
  is_required_for_completion?: boolean;
}): Promise<LessonDetail> {
  return apiRequest<LessonDetail>("/lessons", { method: "POST", body: payload });
}

export function updateLesson(
  id: number,
  payload: Partial<{
    title: string;
    internal_title: string;
    lesson_type: LessonType;
    content_markdown: string;
    video_url: string;
    estimated_minutes: number;
    is_required_for_completion: boolean;
  }>,
): Promise<LessonListItem> {
  return apiRequest<LessonListItem>(`/lessons/${id}`, { method: "PATCH", body: payload });
}

export function deleteLesson(id: number): Promise<void> {
  return apiRequest<void>(`/lessons/${id}`, { method: "DELETE" });
}
